import React from "react";

interface LoaderProps {
  size?: "sm" | "md" | "lg";
  text?: string;
  overlay?: boolean;
}

const GlobalLoader: React.FC<LoaderProps> = ({ 
  size = "md", 
  text = "Loading Data...",
  overlay = true 
}) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16"
  };

  const textSizes = {
    sm: "text-sm",
    md: "text-base",
    lg: "text-lg"
  };

  const loaderContent = (
    <div className="flex flex-col items-center justify-center space-y-4">
      {/* Animated Logo */}
      <div className={`relative ${sizeClasses[size]}`}>
        {/* Outer Ring */}
        <div className="absolute inset-0 border-4 border-blue-200 rounded-full"></div>
        
        {/* Animated Ring */}
        <div className="absolute inset-0 border-4 border-transparent rounded-full border-t-blue-500 border-r-purple-500 animate-spin"></div>
        
        {/* Inner Core */}
        <div className="absolute inset-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
          <div className="w-1/3 h-1/3 bg-white rounded-full animate-pulse"></div>
        </div>
        
        {/* Floating Particles - Fixed positioning */}
        <div className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-blue-400 rounded-full animate-bounce"></div>
        <div className="absolute -bottom-0.5 -left-0.5 w-1.5 h-1.5 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
        <div className="absolute -top-0.5 -left-0.5 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
        <div className="absolute -bottom-0.5 -right-0.5 w-1.5 h-1.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
      </div>

      {/* Text */}
      {text && (
        <div className={`text-center ${textSizes[size]} font-medium text-gray-600 dark:text-gray-300`}>
          <div className="flex items-center space-x-1">
            <span>{text}</span>
            <div className="flex space-x-1">
              <div className="w-1 h-1 bg-blue-500 rounded-full animate-bounce"></div>
              <div className="w-1 h-1 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-1 h-1 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm z-50 flex items-center justify-center overflow-hidden">
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
};

// Alternative Loader with CRM Genix Branding
export const CRMGenixBrandLoader: React.FC<LoaderProps> = ({ 
  size = "md", 
  text = "CRM Genix is working...",
  overlay = false 
}) => {
  const sizeClasses = {
    sm: "w-10 h-10 text-sm",
    md: "w-14 h-14 text-base",
    lg: "w-20 h-20 text-lg"
  };

  const loaderContent = (
    <div className="flex flex-col items-center justify-center space-y-4">
      {/* Animated CRM Genix Logo */}
      <div className={`relative ${sizeClasses[size]} bg-gradient-to-br from-blue-500 via-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden`}>
        {/* Animated Bars */}
        <div className="flex space-x-1">
          <div className="w-1 h-3 bg-white rounded-full animate-pulse"></div>
          <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.1s' }}></div>
          <div className="w-1 h-5 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
          <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
          <div className="w-1 h-3 bg-white rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
        </div>
        
        {/* Rotating Ring - Fixed positioning */}
        <div className="absolute inset-0 border-2 border-transparent border-t-white border-r-white/50 rounded-2xl animate-spin"></div>
      </div>

      {/* Text with Typing Effect */}
      {text && (
        <div className="text-center">
          <div className="text-gray-600 dark:text-gray-300 font-medium">
            {text}
          </div>
          <div className="flex justify-center space-x-1 mt-2">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="w-2 h-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-pulse"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm z-50 flex items-center justify-center overflow-hidden">
        {loaderContent}
      </div>
    );
  }

  return loaderContent;
};

// Page Loader with Progress Bar
export const CRMGenixPageLoader: React.FC<{ message?: string }> = ({ 
  message = "Initializing CRM Genix..." 
}) => {
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900 z-50 flex flex-col items-center justify-center space-y-8 p-4 overflow-hidden">
      {/* Main Logo */}
      <div className="relative w-20 h-20">
        <div className="w-full h-full bg-gradient-to-br from-blue-500 via-purple-600 to-cyan-500 rounded-2xl flex items-center justify-center shadow-2xl">
          <div className="text-white font-bold text-xl">CG</div>
        </div>
        
        {/* Orbiting Elements - Fixed positioning */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-400 rounded-full animate-orbit"></div>
          <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-purple-400 rounded-full animate-orbit" style={{ animationDelay: '1s' }}></div>
          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-cyan-400 rounded-full animate-orbit" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-0 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-indigo-400 rounded-full animate-orbit" style={{ animationDelay: '3s' }}></div>
        </div>
      </div>

      {/* Text Content */}
      <div className="text-center space-y-4 max-w-md">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
          CRM Genix
        </h1>
        <p className="text-gray-600 dark:text-gray-300 text-lg">{message}</p>
      </div>

      {/* Progress Bar */}
      <div className="w-64 max-w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full animate-progress"></div>
      </div>

      {/* Feature Tips */}
      <div className="text-center text-sm text-gray-500 dark:text-gray-400 space-y-1 max-w-md">
        <p>✨ Managing your customer relationships</p>
        <p>🚀 Optimizing your business workflow</p>
        <p>💫 Preparing your dashboard...</p>
      </div>
    </div>
  );
};

// Simple Inline Loader
export const CRMGenixInlineLoader: React.FC<{ text?: string }> = ({ 
  text = "Processing..." 
}) => {
  return (
    <div className="inline-flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
      <div className="flex space-x-1">
        <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
        <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
        <div className="w-2 h-2 bg-cyan-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
      </div>
      <span>{text}</span>
    </div>
  );
};

// Add these CSS animations to your global CSS file
const loaderStyles = `
@keyframes orbit {
  0% { transform: rotate(0deg) translateX(12px) rotate(0deg); }
  100% { transform: rotate(360deg) translateX(12px) rotate(-360deg); }
}

@keyframes progress {
  0% { width: 0%; }
  50% { width: 60%; }
  100% { width: 100%; }
}

.animate-orbit {
  animation: orbit 3s linear infinite;
}

.animate-progress {
  animation: progress 2s ease-in-out infinite;
}

/* Ensure no horizontal scroll */
html, body {
  overflow-x: hidden;
}
`;

// Inject styles (you might want to add this to your main CSS file instead)
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = loaderStyles;
  document.head.appendChild(style);
}

export default GlobalLoader;